import { FC, useState } from 'react'
import { Box, Paper, Skeleton, styled, Typography } from '@mui/material'
import classNames from 'classnames'
import { AttributeLabel } from 'packages/eid-ui'
import { PersonDetails } from 'components/PersonDetails/PersonDetails'
import { useTranslation } from 'react-i18next'
import { Icon, InfoOutline } from 'packages/eid-icons'
import StyledSearchInput from 'packages/eid-ui/SearchInput/StyledSearchInput'
import { isNilOrEmpty } from 'packages/core/utils'

type PeopleListCardProps = {
    className?: any
    data?: any
    isLoading?: any
    label?: string
    enableSearch?: boolean
    pagination?: any
    handleExpand?: any
    expanded?: boolean
    showMore?: boolean
    searchKey?: string
    handleSearch?: (value: string) => void
    personMoreOptions?: any[]
}

const Root = styled('div')({
    position: 'relative',
    minWidth: '32rem',
})

const StyledPaper = styled(Paper)(({ theme }) => ({
    display: 'flex',
    position: 'relative',
    marginTop: '0.8rem',
    alignItems: 'center',
    backgroundColor: '#ffffff !important',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    border: ' solid 1px #d8d8dd',
    borderRadius: '0.4rem',
    padding: '2px 4px',
    height: '3.2rem',
    transition: 'border 0.15s ease-in-out',

    '& .MuiInputBase-root': {
        '& .MuiInputBase-input': {
            fontSize: '1.3rem',
        },
    },

    '&:hover': {
        border: `1px solid ${theme?.palette?.primary?.main} !important`,
    },
    '&:focus-within': {
        border: `1px solid ${theme?.palette?.primary?.main} !important`,
        boxShadow: 'initial',
    },
}))

const StyledIcon = styled(Icon)({
    width: 16,
    height: 16,
    marginLeft: 12,
})

const StyledClearIcon = styled(Icon)({
    margin: '0px 10px !important',
    '&:hover': {
        cursor: 'pointer',
    },
})

const ExpandIcon = styled(Box)(({ theme }) => ({
    width: '1rem',
    height: '1rem',
    marginLeft: '0.5rem',
    marginTop: '-0.0625rem',
    color: theme?.palette?.primary?.main,
}))

const ExpandButton = styled(Box)(() => ({
    display: 'flex',
    textTransform: 'uppercase',
    paddingTop: '0.6rem',
    color: '#0057A0',
    fontSize: '1.2rem',
    '&:hover': {
        cursor: 'pointer',
    },
}))

const SearchIconButton = styled(Box)(() => ({
    display: 'flex',
    textTransform: 'uppercase',
    paddingTop: '0.6rem',
    color: '#0057A0',
    fontSize: '1.2rem',
    marginRight: '1.6rem',
    '&:hover': {
        cursor: 'pointer',
    },
}))

const CancelSearchButton = styled(Box)(() => ({
    display: 'flex',
    textTransform: 'uppercase',
    paddingTop: '0.6rem',
    color: '#8b909a',
    fontSize: '1.2rem',
    marginRight: '1.6rem',
    '&:hover': {
        cursor: 'pointer',
    },
}))

const OwnersContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '30rem',
    overflow: 'auto',
    padding: '0.5rem 0.4rem',
})

const CollapsibleContent = styled(Box)(({ theme }) => ({
    marginTop: '1rem',
    position: 'relative',
    backgroundColor: '#f5f6f7',
    borderRadius: '0.8rem',
    border: 'solid 0.1rem #f9f9f9',
    backdropFilter: 'blur(0.2rem)',
}))

const PersonContainer = styled(Box)({
    padding: '0 1.2rem',
    borderRadius: '0.8rem',
    '&:hover': {
        backgroundColor: '#ffffff',
        '& p': {
            color: '#000000',
            fontWeight: 'bold',
        },
    },
})

const TextRoot = styled(Box)(() => ({
    whiteSpace: 'nowrap',
    marginRight: '0.6rem',
    marginLeft: '0.8rem',
}))
const NotificationRoot = styled(Box)(() => ({
    position: 'absolute',
    top: '-3.3rem',
    left: '0',
    width: 'fit-content !important',
    height: '2.4rem !important',
    margin: '0.8rem 0 0.6rem 0 !important',
    padding: '0 0 0 0.8rem !important',
    borderRadius: '0.5rem !important',
    border: 'solid 0.1rem #f0cf9e !important',
    background: '#ffe6b2 !important',
    '& div': {
        background: '#ffe6b2 !important',
    },
}))

const MIN_CHARACTERS_TO_SEARCH = 3

const PeopleListCard: FC<PeopleListCardProps> = (props) => {
    const { t } = useTranslation()
    const {
        data,
        isLoading,
        label,
        enableSearch = true,
        showMore = true,
        pagination,
        handleExpand,
        expanded,
        searchKey,
        handleSearch,
        personMoreOptions,
    } = props
    const [searchOpen, setSearchOpen] = useState(false)
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    const onSearch = (value: string) => {
        setSearchTerm(value)
        if (
            handleSearch &&
            (value.trim().length >= MIN_CHARACTERS_TO_SEARCH ||
                value.trim().length === 0)
        ) {
            handleSearch(value.trim())
        }
    }

    const MoreButton = () => {
        return (
            <ExpandButton 
                onClick={handleExpand}
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleExpand()
                    }
                }}
            >
                {expanded ? t('Less') : t('More')}
                <ExpandIcon>
                    {expanded ? (
                        <Icon
                            name="MinusCircle"
                            width="1.6rem"
                            height="1.6rem"
                        />
                    ) : (
                        <Icon
                            name="PlusCircle"
                            width="1.6rem"
                            height="1.6rem"
                        />
                    )}
                </ExpandIcon>
            </ExpandButton>
        )
    }

    const SearchButton = () => {
        return searchOpen ? (
            <CancelSearchButton
                display={'flex'}
                alignItems={'center'}
                onClick={() => {
                    handleSearch && handleSearch('')
                    setSearchOpen(false)
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleSearch && handleSearch('')
                        setSearchOpen(false)
                    }
                }}
            >
                <Icon name="Close" width="1.6rem" height="1.6rem" tabIndex={0} />
                <span style={{ marginLeft: '0.6rem' }} tabIndex={0}>{t('Cancel')}</span>
            </CancelSearchButton>
        ) : (
            <SearchIconButton onClick={() => setSearchOpen(true)} tabIndex={0} onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setSearchOpen(true)
                }
            }}>
                <Icon name="Search" width="1.6rem" height="1.6rem" />
            </SearchIconButton>
        )
    }

    const SearchInput = (
        <StyledPaper>
            {isSearchFocused &&
                searchTerm &&
                searchTerm.trim().length < MIN_CHARACTERS_TO_SEARCH && (
                    <NotificationRoot>
                        <Box display="flex" alignItems="center">
                            <Box flex="0.1" display="flex" alignItems="center">
                                <InfoOutline />
                            </Box>
                            <Box flex="1.1">
                                <TextRoot>
                                    <Typography variant="caption">
                                        {t('Three_Character_Search_Message')}
                                    </Typography>
                                </TextRoot>
                            </Box>
                        </Box>
                    </NotificationRoot>
                )}

            <StyledIcon name="Search" color="#D8D8DD" />

            <Box position="relative" sx={{ flex: 1 }}>
                <StyledSearchInput
                    autoFocus
                    value={searchTerm}
                    onChange={(e: any) => onSearch(e.target.value)}
                    placeholder={t('Search')}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                        setIsSearchFocused(false)
                    }}
                    sx={{
                        borderColor: '#000000',
                        '& > input': {
                            width: '100%',
                            height: '10px',
                            border: 'none',
                            boxShadow: 'none !important',
                        },
                    }}
                />
            </Box>
            {!isNilOrEmpty(searchTerm) && (
                <StyledClearIcon
                    name="Close"
                    color="#D8D8DD"
                    onClick={() => onSearch('')}
                />
            )}
        </StyledPaper>
    )

    return (
        <Box marginBottom={'2.4rem'}>
            <Box display={'flex'} alignItems="center" minWidth={'32rem'}>
                {label && (
                    <div>
                        <AttributeLabel
                            sx={{
                                width: 'auto !important',
                                minWidth: '10rem !important',
                            }}
                        >
                            {t(label)}
                        </AttributeLabel>
                    </div>
                )}
                <Box display={'flex'} alignItems="center" marginLeft={'auto'}>
                    {enableSearch && <SearchButton />}
                    {showMore && <MoreButton />}
                </Box>
            </Box>
            {searchOpen && SearchInput}

            <Root className={classNames(props.className)}>
                <CollapsibleContent>
                    <OwnersContainer>
                        {isLoading && <Skeleton height={40} />}
                        {!isLoading && data?.length === 0 && (
                            <Box paddingLeft={'0.4rem'} width={'27rem'}>
                                {t('NoDataFound')}
                            </Box>
                        )}

                        {!isLoading &&
                            data &&
                            data.length > 0 &&
                            data.map((o: any, index: any) => (
                                <Box
                                    component={PersonContainer}
                                    key={o.id + index}
                                >
                                    <PersonDetails
                                        size="small"
                                        person={o}
                                        enabled={true}
                                        personMoreOptions={personMoreOptions}
                                    />
                                </Box>
                            ))}
                    </OwnersContainer>
                    {data && data.length > 0 && (
                        <Box
                            display="flex"
                            alignItems="center"
                            width="100%"
                            justifyContent="center"
                            padding="0.5rem 0"
                        >
                            {pagination}
                        </Box>
                    )}
                </CollapsibleContent>
            </Root>
        </Box>
    )
}

export default PeopleListCard
